import type { DocumentBranding, DocumentContext } from '@/document-render/domain/DocumentContext';
import type { TemplateCoverImage, TemplateCoverImageAlignment, TemplateCoverImageSize } from '@/templates/domain/types/TemplateCoverImage';
import type { TemplateLogo } from '@/templates/domain/types/TemplateLogo';
import { COVER_CUSTOMER_PLACEHOLDER, COVER_DATE_PLACEHOLDER } from '@/document-render/domain/constants/coverPreviewPlaceholders';
import { resolveContextCustomerLocale } from '@/document-render/domain/customerLocaleFormatters';
import { formatCoverDisplayDate } from '@/document-render/application/utils/formatCoverDisplayDate';
import { assertSafeImageUrl } from '@/common/domain/utils/assertSafeImageUrl';
import { COVER_IMAGE_DEFAULT_ALIGNMENT, COVER_IMAGE_DEFAULT_SIZE } from '@/document-render/application/coverImageLayout';

/** Sample/fallback preview must not look like a finished quote with mock customer/date. */
export function isCoverPreviewUnbound(context: DocumentContext): boolean {
  return context.previewValueBinding === 'unbound';
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

function trimOptionalString(value: unknown): string | null | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value !== 'string') return undefined;
  return value;
}

const LOGO_POSITIONS = new Set(['header', 'footer']);
const LOGO_SIZES = new Set(['small', 'medium', 'large']);
const LOGO_ALIGNMENTS = new Set(['left', 'center', 'right']);
const COVER_IMAGE_SIZES = new Set(['small', 'medium', 'full']);

/** Narrows `context.branding` from the permissive bag into a typed DocumentBranding. */
export function readDocumentBranding(context: DocumentContext): DocumentBranding {
  return normalizeDocumentBranding(context.branding);
}

function trimSafeLogoUrl(value: string | null | undefined): string | null {
  if (value == null || typeof value !== 'string') return null;
  return assertSafeImageUrl(value);
}

/**
 * Reads the logo URL already merged into `context.branding` by DocumentRender.
 * Block renderers must use this — not `resolveLogoUrl(undefined, context)` — so template-only
 * previews do not re-apply the RapidWorks default fallback.
 */
export function readResolvedLogoUrlFromContext(context: DocumentContext): string | null {
  const branding = readDocumentBranding(context);
  return trimSafeLogoUrl(branding.logoUrl) ?? trimSafeLogoUrl(branding.logo);
}

/** Parses a raw host/API branding object into DocumentBranding (unknown keys dropped). */
export function normalizeDocumentBranding(raw: unknown): DocumentBranding {
  if (!isPlainObject(raw)) return {};

  const branding: DocumentBranding = {};

  const logoUrl = trimOptionalString(raw.logoUrl);
  if (logoUrl !== undefined) branding.logoUrl = logoUrl;

  const logo = trimOptionalString(raw.logo);
  if (logo !== undefined) branding.logo = logo;

  const customerName = trimOptionalString(raw.customerName);
  if (customerName !== undefined) branding.customerName = customerName;

  const date = trimOptionalString(raw.date);
  if (date !== undefined) branding.date = date;

  if (typeof raw.logoPosition === 'string' && LOGO_POSITIONS.has(raw.logoPosition)) {
    branding.logoPosition = raw.logoPosition as DocumentBranding['logoPosition'];
  }
  if (typeof raw.logoSize === 'string' && LOGO_SIZES.has(raw.logoSize)) {
    branding.logoSize = raw.logoSize as DocumentBranding['logoSize'];
  }
  if (typeof raw.logoAlignment === 'string' && LOGO_ALIGNMENTS.has(raw.logoAlignment)) {
    branding.logoAlignment = raw.logoAlignment as DocumentBranding['logoAlignment'];
  }

  const coverImageUrl = trimOptionalString(raw.coverImageUrl);
  if (coverImageUrl !== undefined) branding.coverImageUrl = coverImageUrl;

  if (typeof raw.coverImageSize === 'string' && COVER_IMAGE_SIZES.has(raw.coverImageSize)) {
    branding.coverImageSize = raw.coverImageSize as DocumentBranding['coverImageSize'];
  }
  if (typeof raw.coverImageAlignment === 'string' && LOGO_ALIGNMENTS.has(raw.coverImageAlignment)) {
    branding.coverImageAlignment = raw.coverImageAlignment as DocumentBranding['coverImageAlignment'];
  }
  if (typeof raw.coverImageRepeat === 'boolean') branding.coverImageRepeat = raw.coverImageRepeat;

  const coverImageAltText = trimOptionalString(raw.coverImageAltText);
  if (coverImageAltText !== undefined) branding.coverImageAltText = coverImageAltText;

  return branding;
}

/** Shallow-merge branding patches (host → template enrichment). Later wins per field. */
export function mergeDocumentBranding(base: DocumentBranding | undefined, patch: DocumentBranding): DocumentBranding {
  return { ...(base ?? {}), ...patch };
}

/** Removes host-injected logo URLs so template-only surfaces do not show tenant/company marks. */
export function stripHostLogoFromBranding(context: DocumentContext): DocumentContext {
  const branding = readDocumentBranding(context);
  if (!branding.logoUrl && !branding.logo) return context;

  const { logoUrl: _logoUrl, logo: _logo, ...rest } = branding;
  return { ...context, branding: rest };
}

/**
 * Merges template logo placement + resolved URL into context.branding so cover/header/footer
 * renderers share one typed bag on HTML and PDF.
 */
export function enrichDocumentContextWithTemplateLogo(
  context: DocumentContext,
  logo: TemplateLogo | null | undefined,
  logoUrl: string | null
): DocumentContext {
  if (!logo && !logoUrl) return context;

  const existing = readDocumentBranding(context);
  const patch: DocumentBranding = {};
  if (logoUrl) patch.logoUrl = logoUrl;
  if (logo) {
    patch.logoPosition = logo.position;
    patch.logoSize = logo.size;
    patch.logoAlignment = logo.alignment;
  }

  return {
    ...context,
    branding: mergeDocumentBranding(existing, patch)
  };
}

/**
 * Merges the template's cover-page image into `context.branding` so the cover renderers share one
 * typed bag on HTML and PDF (same route as the logo). A cover image with no renderable URL — a blob
 * key the host has not resolved, or an unsafe value — adds nothing: the cover draws without it.
 */
export function enrichDocumentContextWithTemplateCoverImage(
  context: DocumentContext,
  coverImage: TemplateCoverImage | null | undefined
): DocumentContext {
  if (!coverImage) return context;
  const url = assertSafeImageUrl(coverImage.imageUrl) ?? assertSafeImageUrl(coverImage.assetRef);
  if (!url) return context;

  return {
    ...context,
    branding: mergeDocumentBranding(readDocumentBranding(context), {
      coverImageUrl: url,
      coverImageSize: coverImage.size,
      coverImageAlignment: coverImage.alignment,
      coverImageRepeat: coverImage.repeat,
      coverImageAltText: coverImage.altText
    })
  };
}

/** The cover image a cover renderer draws: a safe URL plus its layout settings, or null for none. */
export interface DocumentCoverImage {
  url: string;
  size: TemplateCoverImageSize;
  alignment: TemplateCoverImageAlignment;
  repeat: boolean;
  /** Empty when the author gave no description. */
  altText: string;
}

export function readCoverImageFromContext(context: DocumentContext): DocumentCoverImage | null {
  const branding = readDocumentBranding(context);
  const url = trimSafeLogoUrl(branding.coverImageUrl);
  if (!url) return null;

  return {
    url,
    size: branding.coverImageSize ?? COVER_IMAGE_DEFAULT_SIZE,
    alignment: branding.coverImageAlignment ?? COVER_IMAGE_DEFAULT_ALIGNMENT,
    repeat: branding.coverImageRepeat === true,
    altText: branding.coverImageAltText?.trim() ?? ''
  };
}

/** Cover customer name: branding override, then customer.name. Unbound sample → token placeholder. */
export function resolveCoverCustomerName(context: DocumentContext): string {
  if (isCoverPreviewUnbound(context)) return COVER_CUSTOMER_PLACEHOLDER;
  const fromBranding = readDocumentBranding(context).customerName?.trim() ?? '';
  if (fromBranding) return fromBranding;
  return (context.customer?.name ?? '').trim();
}

/**
 * Cover date: branding override, then quote.valid_until (expiration), then quote.date,
 * then date.today — formatted per customerLocale (US MM/dd/yyyy when unset).
 *
 * Quote Builder drives cover Date from Quote Expiration Date via `quote.valid_until`.
 */
export function resolveCoverDate(context: DocumentContext): string {
  if (isCoverPreviewUnbound(context)) return COVER_DATE_PLACEHOLDER;
  const locale = resolveContextCustomerLocale(context as Record<string, unknown>);
  const fromBranding = readDocumentBranding(context).date?.trim() ?? '';
  if (fromBranding) return formatCoverDisplayDate(fromBranding, locale);
  const fromQuote = context.quote?.valid_until?.trim() || context.quote?.date?.trim() || context.date?.today?.trim() || '';
  return formatCoverDisplayDate(fromQuote, locale);
}
